"use client";

import clsx from "clsx";
import {useCallback, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Button} from "@/components/ui/button";
import {Dialog, DialogContent, DialogHeader, DialogTitle} from "@/components/ui/dialog";
import {formatDateTimeDisplay} from "@/components/workflows/custom-nodes/delay/delayUtils";
import {SupportedLanguages} from "@/enums/internationalization";
import {useOrg} from "@/hooks/useOrg";
import {useUserProfile} from "@/hooks/useUserProfile";
import {isRelativeUrl, NotificationItem, NotificationPage} from "@/types/notifications";
import { apiFetch } from "@/lib/api";
import {GetTextFromLangString} from "@/utils/common/translations";
import RecentCallNotification from "./renderers/RecentCallNotification";

type NotificationCenterDialogProps = {
   open: boolean;
   onOpenChange: (open: boolean) => void;
};

export default function NotificationCenterDialog({
   open,
   onOpenChange,
}: NotificationCenterDialogProps) {
   const {t} = useTranslation("notifications");
   const {t: ct} = useTranslation("common");
   const {ui} = useOrg();
   const {userProfile} = useUserProfile();
   const [page, setPage] = useState<NotificationPage | null>(null);
   const [loading, setLoading] = useState(false);
   const [loadingMore, setLoadingMore] = useState(false);
   const [filter, setFilter] = useState<"unread" | "read">("unread");
   const profileLang = (userProfile?.language as SupportedLanguages) ?? SupportedLanguages.English;

   const fetchPage = useCallback(
      async (pageIndex: number, append: boolean, readFilter: "unread" | "read") => {
         if (append) setLoadingMore(true);
         else setLoading(true);
         try {
            const res = await apiFetch<NotificationPage>(`/api/notifications?page=${pageIndex}&filter=${readFilter}`, "GET");
            if (res) {
               setPage((prev) => {
                  if (append && prev) {
                     return {
                        notifications: [...prev.notifications, ...res.notifications],
                        nextCursor: res.nextCursor,
                        total: res.total,
                     };
                  }
                  return res;
               });
            }
         } finally {
            setLoading(false);
            setLoadingMore(false);
         }
      },
      []
   );

   useEffect(() => {
      if (open) {
         setFilter("unread");
         fetchPage(0, false, "unread");
      }
   }, [open, fetchPage]);

   const handleFilterChange = useCallback(
      (newFilter: "unread" | "read") => {
         setFilter(newFilter);
         setPage(null);
         fetchPage(0, false, newFilter);
      },
      [fetchPage]
   );

   const handleMarkRead = useCallback(
      async (id: string) => {
         await apiFetch(`/api/notifications/${id}`, "PATCH", { body: {isRead: true} });
         setPage((prev) => {
            if (!prev) return null;
            if (filter === "unread") {
               return {
                  ...prev,
                  notifications: prev.notifications.filter((n) => n.id !== id),
               };
            }
            return {
               ...prev,
               notifications: prev.notifications.map((n) =>
                  n.id === id ? {...n, isRead: true} : n
               ),
            };
         });
      },
      [filter]
   );

   const handleDelete = useCallback(async (id: string) => {
      await apiFetch(`/api/notifications/${id}`, "DELETE");
      setPage((prev) =>
         prev
            ? {
                 ...prev,
                 notifications: prev.notifications.filter((n) => n.id !== id),
                 total: Math.max(0, prev.total - 1),
              }
            : null
      );
   }, []);

   const renderNotification = (n: NotificationItem) => {
      if (n.type === "recentCall" && n.data) {
         const data = n.data as {
            callId?: string;
            direction?: "inbound" | "outbound";
            toPhoneNumber?: string;
            fromPhoneNumber?: string;
         };
         return (
            <RecentCallNotification
               data={{
                  callId: data.callId ?? "",
                  direction: data.direction ?? "inbound",
                  toPhoneNumber: data.toPhoneNumber ?? "",
                  fromPhoneNumber: data.fromPhoneNumber ?? "",
               }}
               createdAt={n.createdAt}
               country={ui.country}
            />
         );
      }
      const desc = GetTextFromLangString(n.description, profileLang) || t("noDescription");
      if (n.type === "link" && n.data) {
         const url = (n.data as { url?: string }).url;
         if (url && isRelativeUrl(url)) {
            return (
               <a href={url} className="text-blue-600 dark:text-blue-400 hover:underline">
                  {desc}
               </a>
            );
         }
      }
      return <span>{desc}</span>;
   };

   const emptyMessage = filter === "unread" ? t("noUnread") : t("noRead");

   return (
      <Dialog open={open} onOpenChange={onOpenChange}>
         <DialogContent className="flex flex-col max-w-md max-h-[80vh]">
            <DialogHeader>
               <DialogTitle>{t("title")}</DialogTitle>
            </DialogHeader>
            <div className="flex gap-1 pb-2 border-200 border-b">
               <Button
                  variant={filter === "unread" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => handleFilterChange("unread")}
               >
                  {t("unread")}
               </Button>
               <Button
                  variant={filter === "read" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => handleFilterChange("read")}
               >
                  {t("read")}
               </Button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
               {loading && <p className={clsx("text-sm", "text-600")}>{ct("loading")}</p>}
               {!loading && page && page.notifications.length === 0 && (
                  <p className={clsx("text-sm", "text-600")}>{emptyMessage}</p>
               )}
               {!loading && page && page.notifications.length > 0 && (
                  <ul className="space-y-3">
                     {page.notifications.map((n) => (
                        <li
                           key={n.id}
                           className={clsx(
                              "p-3",
                              "rounded-lg",
                              "border",
                              "border-200",
                              !n.isRead && "bg-slate-50 dark:bg-slate-900"
                           )}
                        >
                           <div className="flex justify-between items-start gap-2">
                              {!n.isRead ? (
                                 <button
                                    type="button"
                                    className="flex-1 min-w-0 cursor-pointer text-left"
                                    onClick={() => handleMarkRead(n.id)}
                                    aria-label={t("markAsRead")}
                                 >
                                    <div className="text-sm">{renderNotification(n)}</div>
                                    <p className="mt-1 text-gray-500 dark:text-gray-400 text-xs">
                                       {formatDateTimeDisplay(n.createdAt)}
                                    </p>
                                 </button>
                              ) : (
                                 <div className="flex-1 min-w-0">
                                    <div className="text-sm">{renderNotification(n)}</div>
                                    <p className="mt-1 text-gray-500 dark:text-gray-400 text-xs">
                                       {formatDateTimeDisplay(n.createdAt)}
                                    </p>
                                 </div>
                              )}
                              <div className="flex flex-shrink-0 items-center gap-1">
                                 {!n.isRead && (
                                    <Button
                                       variant="ghost"
                                       size="sm"
                                       onClick={() => handleMarkRead(n.id)}
                                    >
                                       {t("markAsRead")}
                                    </Button>
                                 )}
                                 <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete(n.id)}
                                 >
                                    {t("delete")}
                                 </Button>
                              </div>
                           </div>
                        </li>
                     ))}
                  </ul>
               )}
               {page?.nextCursor != null && !loading && (
                  <div className="flex justify-center mt-4">
                     <Button
                        variant="secondary"
                        disabled={loadingMore}
                        onClick={() => fetchPage(page.nextCursor!, true, filter)}
                     >
                        {loadingMore ? ct("loading") : t("loadMore")}
                     </Button>
                  </div>
               )}
            </div>
         </DialogContent>
      </Dialog>
   );
}
